// src/components/marketing/CampaignsList.tsx
// Track E2 — lists the org's Meta Ads campaigns with filters, pagination,
// and per-row actions (launch / pause / resume / delete / sync).
//
// The detail drill-in (metrics charts, ad sets, ads) is a follow-up PR.
// Create-wizard is also a separate PR; this list has a "Nueva campaña"
// button that for now opens a minimal modal with just {name, objective,
// daily_budget_cents} so ops can author drafts end-to-end.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, Check, PauseCircle, CircleSlash,
  Loader2, Play, Plus, RefreshCw, Search, Trash2, Zap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  ApiError, createCampaign, deleteCampaign,
  launchCampaign, listCampaigns, pauseCampaign, refreshCampaignStates,
  resumeCampaign, syncCampaign,
  type Campaign, type CampaignCreateIn, type CampaignObjective,
  type CampaignStatus, type ListCampaignsFilters, type Paginated,
} from '@/lib/api_campaigns';

const PAGE_SIZE = 25;

const STATUS_OPTIONS: Array<{ value: CampaignStatus | ''; label: string }> = [
  { value: '', label: 'Todos los estados' },
  { value: 'draft', label: 'Borrador' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'active', label: 'Activa' },
  { value: 'paused', label: 'Pausada' },
  { value: 'completed', label: 'Completada' },
  { value: 'error', label: 'Error' },
];

const OBJECTIVE_OPTIONS: Array<{ value: CampaignObjective; label: string }> = [
  { value: '', label: 'Todos los objetivos' },
  { value: 'AWARENESS', label: 'Awareness' },
  { value: 'TRAFFIC', label: 'Tráfico' },
  { value: 'ENGAGEMENT', label: 'Engagement' },
  { value: 'LEADS', label: 'Leads' },
  { value: 'APP_PROMOTION', label: 'App' },
  { value: 'SALES', label: 'Ventas' },
  { value: 'MESSAGES', label: 'Mensajes' },
];

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void;

interface Props {
  addNotification?: Notifier;
}

export function CampaignsList({ addNotification }: Props) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ListCampaignsFilters>({});
  const [page, setPage] = useState(0);
  const [data, setData] = useState<Paginated<Campaign> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<Record<number, string>>({});
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listCampaigns({
        ...filters,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setData(res);
    } catch (e: any) {
      const msg = e?.message || 'No se pudo cargar la lista de campañas.';
      setError(msg);
      addNotification?.({ type: 'error', title: 'Error', message: msg });
    } finally {
      setLoading(false);
    }
  }, [filters, page, addNotification]);

  useEffect(() => { load(); }, [load]);

  const resetPageAnd = (patch: Partial<ListCampaignsFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(0);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  const handleAction = async (
    campaign: Campaign,
    action: 'launch' | 'pause' | 'resume' | 'sync' | 'delete',
  ) => {
    const label = ACTION_LABEL[action];
    if (action === 'delete') {
      const ok = window.confirm(
        `¿Borrar la campaña "${campaign.name}"? Esta acción NO borra la campaña en Meta; si ya fue publicada, primero pausa.`,
      );
      if (!ok) return;
    }

    setPendingAction((m) => ({ ...m, [campaign.id]: action }));
    try {
      switch (action) {
        case 'launch':  await launchCampaign(campaign.id); break;
        case 'pause':   await pauseCampaign(campaign.id); break;
        case 'resume':  await resumeCampaign(campaign.id); break;
        case 'sync':    await syncCampaign(campaign.id, 'last_7d'); break;
        case 'delete':  await deleteCampaign(campaign.id); break;
      }
      addNotification?.({
        type: 'success',
        title: `${label} encolada`,
        message: action === 'delete' ? 'Campaña eliminada.' : `"${campaign.name}" — revisa el estado en unos segundos.`,
        duration: 4000,
      });
      load();
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error desconocido');
      addNotification?.({ type: 'error', title: `No se pudo ${label.toLowerCase()}`, message: msg });
    } finally {
      setPendingAction((m) => {
        const { [campaign.id]: _dropped, ...rest } = m;
        return rest;
      });
    }
  };

  const handleCreate = async (data: CampaignCreateIn) => {
    try {
      const created = await createCampaign(data);
      addNotification?.({
        type: 'success',
        title: 'Campaña creada',
        message: `"${created.name}" está en borrador. Lánzala cuando estés listo.`,
      });
      setCreateOpen(false);
      load();
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error desconocido');
      addNotification?.({ type: 'error', title: 'No se pudo crear', message: msg });
    }
  };

  return (
    <div className="o-card p-5">
      <header className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Activity size={18} />
          Campañas Meta Ads
        </h1>
        <div className="flex-1" />
        <button
          type="button"
          onClick={async () => {
            try {
              const r = await refreshCampaignStates();
              addNotification?.({
                type: 'success',
                title: 'Estados sincronizados',
                message: `${r.updated ?? 0} campañas actualizadas desde Meta`,
              });
              await load();
            } catch (e: any) {
              const msg = e?.message || 'No se pudo refrescar contra Meta';
              addNotification?.({ type: 'error', title: 'Error', message: msg });
            }
          }}
          className="o-btn-ghost flex items-center gap-1 text-sm"
          title="Sincroniza el estado/budget de cada campaña con Meta"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Sincronizar con Meta
        </button>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="px-3 py-1.5 rounded-lg bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)] hover:bg-[var(--acc1,#00e5ff)]/30 flex items-center gap-1 text-sm font-medium"
        >
          <Plus size={14} />
          Nueva campaña
        </button>
      </header>

      <Filters filters={filters} onChange={resetPageAnd} />

      {error && (
        <div className="mt-4 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-300 flex items-start gap-2">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-medium">Error al cargar campañas</div>
            <div className="opacity-80">{error}</div>
          </div>
        </div>
      )}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-white/50">
            <tr>
              <Th>Nombre</Th>
              <Th>Estado</Th>
              <Th>Objetivo</Th>
              <Th align="right">Presupuesto diario</Th>
              <Th align="right">Gasto</Th>
              <Th align="right">Impresiones</Th>
              <Th align="right">Clicks</Th>
              <Th align="right">CTR</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {loading && !data && (
              <tr><td colSpan={9} className="text-center py-10 opacity-60">
                <Loader2 className="inline animate-spin" size={18} />
              </td></tr>
            )}
            {!loading && data && data.items.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 opacity-60">
                Sin campañas. <button className="underline" onClick={() => setCreateOpen(true)}>Crea la primera</button>.
              </td></tr>
            )}
            {data?.items.map((c) => (
              <CampaignRow
                key={c.id}
                campaign={c}
                pending={pendingAction[c.id]}
                onAction={(a) => handleAction(c, a)}
                onOpen={() => navigate(`/m/mkt-meta/campaigns/${c.id}`)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {data && data.count > PAGE_SIZE && (
        <footer className="flex items-center justify-between mt-4 text-xs text-white/60">
          <div>
            {data.count.toLocaleString()} campañas · página {page + 1} de {totalPages}
          </div>
          <div className="flex gap-1">
            <PageButton disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
              Anterior
            </PageButton>
            <PageButton disabled={page + 1 >= totalPages || loading} onClick={() => setPage((p) => p + 1)}>
              Siguiente
            </PageButton>
          </div>
        </footer>
      )}

      {createOpen && (
        <CreateModal
          onClose={() => setCreateOpen(false)}
          onSubmit={handleCreate}
        />
      )}
    </div>
  );
}

// ── Filters row ──────────────────────────────────────────────

function Filters({
  filters,
  onChange,
}: {
  filters: ListCampaignsFilters;
  onChange: (patch: Partial<ListCampaignsFilters>) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm">
        <Search size={14} className="opacity-60" />
        <input
          type="text"
          placeholder="Buscar nombre…"
          defaultValue={filters.q ?? ''}
          onChange={(e) => onChange({ q: e.target.value || undefined })}
          className="bg-transparent outline-none w-48"
        />
      </label>
      <Select
        value={filters.status ?? ''}
        onChange={(v) => onChange({ status: (v || undefined) as CampaignStatus | undefined })}
        options={STATUS_OPTIONS}
      />
      <Select
        value={filters.objective ?? ''}
        onChange={(v) => onChange({ objective: (v || undefined) as CampaignObjective | undefined })}
        options={OBJECTIVE_OPTIONS}
      />
      <Toggle
        checked={!!filters.is_favorite}
        onChange={(v) => onChange({ is_favorite: v || undefined })}
        label="★ favoritas"
      />
      <Toggle
        checked={!!filters.is_boost}
        onChange={(v) => onChange({ is_boost: v || undefined })}
        label="⚡ boosts"
      />
    </div>
  );
}

function Select({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-[var(--panel,#1e1f2b)]">
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className={`px-2 py-1.5 rounded-md border text-sm cursor-pointer select-none ${
      checked
        ? 'bg-[var(--acc1,#00e5ff)]/20 border-[var(--acc1,#00e5ff)]/50 text-[var(--acc1,#00e5ff)]'
        : 'bg-white/5 border-white/10 text-white/60 hover:text-white'
    }`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="hidden"
      />
      {label}
    </label>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th className={`py-2 px-2 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}

// ── Status badge ─────────────────────────────────────────────

const STATUS_STYLES: Record<CampaignStatus, { bg: string; label: string; icon: React.ReactNode }> = {
  draft:     { bg: 'bg-white/10 text-white/70',          label: 'Borrador',   icon: <CircleSlash size={12} /> },
  pending:   { bg: 'bg-amber-500/15 text-amber-300',     label: 'Pendiente',  icon: <Loader2 size={12} className="animate-spin" /> },
  active:    { bg: 'bg-emerald-500/15 text-emerald-300', label: 'Activa',     icon: <Play size={12} /> },
  paused:    { bg: 'bg-sky-500/15 text-sky-300',         label: 'Pausada',    icon: <PauseCircle size={12} /> },
  completed: { bg: 'bg-purple-500/15 text-purple-300',   label: 'Completada', icon: <Check size={12} /> },
  error:     { bg: 'bg-red-500/15 text-red-300',         label: 'Error',      icon: <AlertTriangle size={12} /> },
};

function StatusBadge({ status }: { status: CampaignStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${s.bg}`}>
      {s.icon}
      {s.label}
    </span>
  );
}

// ── Row ──────────────────────────────────────────────────────

const ACTION_LABEL: Record<'launch' | 'pause' | 'resume' | 'sync' | 'delete', string> = {
  launch: 'Lanzar',
  pause: 'Pausar',
  resume: 'Reanudar',
  sync: 'Sincronizar',
  delete: 'Eliminar',
};

function CampaignRow({
  campaign,
  pending,
  onAction,
  onOpen,
}: {
  campaign: Campaign;
  pending?: string;
  onAction: (a: 'launch' | 'pause' | 'resume' | 'sync' | 'delete') => void;
  onOpen: () => void;
}) {
  const ctr = useMemo(() => {
    if (!campaign.total_impressions) return null;
    return (campaign.total_clicks / campaign.total_impressions) * 100;
  }, [campaign.total_clicks, campaign.total_impressions]);

  const canLaunch  = ['draft', 'error', 'paused'].includes(campaign.status);
  const canPause   = !!campaign.meta_campaign_id && campaign.status === 'active';
  const canResume  = !!campaign.meta_campaign_id && campaign.status === 'paused';
  const canSync    = !!campaign.meta_campaign_id;
  const isPending  = !!pending;

  return (
    <tr className="border-t border-white/5 hover:bg-white/5">
      <td className="py-2 px-2">
        <button
          type="button"
          onClick={onOpen}
          className="flex items-center gap-2 max-w-[280px] w-full text-left"
          title="Ver detalle"
        >
          {campaign.is_boost && <Zap size={12} className="text-amber-300 shrink-0" aria-label="Boost" />}
          {campaign.is_favorite && <span className="text-amber-300" aria-label="Favorita">★</span>}
          <div className="min-w-0">
            <div className="truncate font-medium text-white hover:text-[var(--acc1,#00e5ff)]">{campaign.name}</div>
            {campaign.error_message && (
              <div className="truncate text-xs text-red-300/80" title={campaign.error_message}>
                {campaign.error_message}
              </div>
            )}
          </div>
        </button>
      </td>
      <td className="py-2 px-2"><StatusBadge status={campaign.status} /></td>
      <td className="py-2 px-2 text-white/70">
        {campaign.objective || '—'}
      </td>
      <td className="py-2 px-2 text-right">{formatCents(campaign.daily_budget_cents)}</td>
      <td className="py-2 px-2 text-right">{formatCents(campaign.total_spend_cents)}</td>
      <td className="py-2 px-2 text-right">{campaign.total_impressions.toLocaleString()}</td>
      <td className="py-2 px-2 text-right">{campaign.total_clicks.toLocaleString()}</td>
      <td className="py-2 px-2 text-right">{ctr !== null ? `${ctr.toFixed(2)}%` : '—'}</td>
      <td className="py-2 px-2">
        <div className="flex items-center gap-1">
          <IconAction
            title="Lanzar / relanzar"
            disabled={!canLaunch || isPending}
            pending={pending === 'launch'}
            onClick={() => onAction('launch')}
            Icon={Play}
          />
          <IconAction
            title="Pausar"
            disabled={!canPause || isPending}
            pending={pending === 'pause'}
            onClick={() => onAction('pause')}
            Icon={PauseCircle}
          />
          <IconAction
            title="Reanudar"
            disabled={!canResume || isPending}
            pending={pending === 'resume'}
            onClick={() => onAction('resume')}
            Icon={Activity}
          />
          <IconAction
            title="Sincronizar métricas"
            disabled={!canSync || isPending}
            pending={pending === 'sync'}
            onClick={() => onAction('sync')}
            Icon={RefreshCw}
          />
          <IconAction
            title="Eliminar"
            disabled={isPending}
            pending={pending === 'delete'}
            onClick={() => onAction('delete')}
            Icon={Trash2}
            danger
          />
        </div>
      </td>
    </tr>
  );
}

function IconAction({
  Icon, title, onClick, disabled, pending, danger,
}: {
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  pending?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled || pending}
      className={`p-1.5 rounded-md transition-colors ${
        disabled
          ? 'opacity-30 cursor-not-allowed'
          : danger
          ? 'hover:bg-red-500/20 text-red-300'
          : 'hover:bg-white/10 text-white/70 hover:text-white'
      }`}
    >
      {pending ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />}
    </button>
  );
}

function PageButton({
  children, disabled, onClick,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`px-2 py-1 rounded-md border text-xs ${
        disabled
          ? 'border-white/5 opacity-40 cursor-not-allowed'
          : 'border-white/10 hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  );
}

// ── Create modal ─────────────────────────────────────────────

function CreateModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (data: CampaignCreateIn) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [objective, setObjective] = useState<CampaignObjective>('SALES');
  const [dailyBudget, setDailyBudget] = useState<string>('50');
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    const cents = Math.max(0, Math.round(parseFloat(dailyBudget) * 100));
    setSaving(true);
    try {
      await onSubmit({
        name: trimmed,
        objective,
        daily_budget_cents: cents || null,
      });
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-xl border border-white/10 bg-[var(--panel,#1e1f2b)] p-5"
      >
        <h2 className="text-base font-semibold mb-4">Nueva campaña</h2>

        <label className="block mb-3">
          <span className="text-xs uppercase tracking-wider text-white/50">Nombre</span>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Primavera 2026 — retargeting Yucatán"
            className="mt-1 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50"
          />
        </label>

        <label className="block mb-3">
          <span className="text-xs uppercase tracking-wider text-white/50">Objetivo</span>
          <select
            value={objective}
            onChange={(e) => setObjective(e.target.value as CampaignObjective)}
            className="mt-1 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none"
          >
            {OBJECTIVE_OPTIONS.filter((o) => o.value).map((o) => (
              <option key={o.value} value={o.value} className="bg-[var(--panel,#1e1f2b)]">
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block mb-5">
          <span className="text-xs uppercase tracking-wider text-white/50">
            Presupuesto diario (USD)
          </span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={dailyBudget}
            onChange={(e) => setDailyBudget(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50"
          />
          <span className="text-xs text-white/40 mt-1 block">
            Se guarda en centavos. Déjalo en 0 si usarás presupuesto total (lifetime).
          </span>
        </label>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 text-sm"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className="px-3 py-1.5 rounded-md bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)] hover:bg-[var(--acc1,#00e5ff)]/30 text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Creando…' : 'Crear borrador'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Utilities ────────────────────────────────────────────────

function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const dollars = cents / 100;
  return `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default CampaignsList;
